import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { smsProgram } from "@/lib/sms-program";

/**
 * Terms for the appointment text-message program, linked from the opt-in checkbox on the booking
 * form. Public on purpose: carrier review follows the link from the opt-in without signing in, and
 * a page behind the login reads to them as no page at all.
 */
export default function SmsTermsPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto space-y-6 text-slate-700">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: "#009681" }}>
              Text Message Terms of Service
            </h1>
            <p className="mt-1 text-sm text-slate-500">Last updated: September 10, 2026</p>
          </div>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">The program</h2>
            <p>
              {smsProgram.programName} is a text message service from {smsProgram.organizationName} for people
              who have scheduled an Energy Assistance appointment. You join by checking the text message
              box when you book an appointment on this website, by asking for texts on our automated phone
              system, or by asking staff at your appointment.
            </p>
            <p>
              Consent to receive these texts is not a condition of making an appointment or of receiving
              any assistance. You can complete a booking without it.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Messages you will receive</h2>
            <ul className="list-disc list-inside space-y-1">
              {smsProgram.messageTypes.map((messageType) => (
                <li key={messageType}>{messageType}</li>
              ))}
            </ul>
            <p>
              We do not send marketing or fundraising texts through this program.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Message frequency</h2>
            <p>{smsProgram.frequency}</p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Cost</h2>
            <p>
              {smsProgram.organizationName} does not charge for these messages. {smsProgram.rates} Check with
              your mobile carrier about your plan.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">How to stop messages</h2>
            <p>
              Reply <strong>STOP</strong> to any message from us to stop receiving texts. You will get one
              message confirming that you have been unsubscribed, and then no further texts. Stopping texts
              does not cancel your appointment, and we will still reach you by phone or email about it.
            </p>
            <p>
              To start receiving texts again, reply <strong>START</strong>, check the text message box the
              next time you book an appointment, or ask staff at your appointment.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">How to get help</h2>
            <p>
              Reply <strong>HELP</strong> to any message from us and we will reply with instructions. You
              can also reply to any message from us with a question about your appointment.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Delivery</h2>
            <p>
              {smsProgram.carrierLiability} Text messages depend on your carrier's network and on the phone
              number you gave us being current, so do not rely on a text alone to know when your appointment
              is. Your confirmation number and appointment details are always available on this website.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Your privacy</h2>
            <p>
              Your mobile number and your consent to receive texts are not sold, and are not shared with
              anyone for their own marketing or promotional purposes. See our{" "}
              <Link className="underline" to={smsProgram.privacyPath}>
                Privacy Policy
              </Link>{" "}
              for what we collect and who we share it with.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Contact us</h2>
            <p>
              These messages come from {smsProgram.organizationName} Energy Assistance. Reply to any message
              we send you, or reply HELP, to reach us about this program.
            </p>
          </section>

          <p className="pt-2">
            <Link className="underline" to="/booking">
              Back to appointment scheduling
            </Link>
          </p>
        </div>
      </main>

      <PublicFooter>Multi-Service Center &nbsp;·&nbsp; Energy Assistance</PublicFooter>
    </div>
  );
}
